import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Wifi, Coffee, Users, Printer, Plug, Sparkles, Backpack, Armchair } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { eur, spaceTypeLabel, formatApiError } from "../lib/utils.helpers";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";

const INCLUDED = [
  { icon: Wifi, label: "WiFi haut débit" },
  { icon: Coffee, label: "Café" },
  { icon: Users, label: "Animations régulières" },
  { icon: Printer, label: "Équipements inclus" },
  { icon: Plug, label: "Prises & USB à disposition" },
];

export default function Tarifs() {
  const [spaces, setSpaces] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { api.get("/spaces").then((r) => setSpaces(r.data)); }, []);

  const subscribePass = async (passType) => {
    if (!user) { navigate("/connexion?next=/tarifs"); return; }
    try {
      const { data } = await api.post("/passes/purchase", {
        pass_type: passType,
        payment_method: "card",
        origin_url: window.location.origin,
      });
      if (data.url) window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const subscribeOnSite = async (passType) => {
    if (!user) { navigate("/connexion?next=/tarifs"); return; }
    try {
      await api.post("/passes/purchase", { pass_type: passType, payment_method: "on_site" });
      toast.success("Pass souscrit — à régler sur place. Il sera activé après paiement.");
      navigate("/mon-compte");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="container-x py-16" data-testid="tarifs-page">
      <p className="overline text-[#2B8B8E]">Tarifs</p>
      <h1 className="text-4xl sm:text-5xl font-light mt-2 mb-3">Des solutions flexibles pour tous vos besoins.</h1>
      <p className="text-muted-foreground max-w-2xl mb-12">Aucun engagement, annulation possible jusqu'à 24h avant.</p>

      {/* SECTION À LA CARTE */}
      <div className="flex items-center gap-3 mb-6">
        <span className="w-9 h-9 rounded-full bg-[#2B8B8E] text-white text-sm font-bold flex items-center justify-center">1</span>
        <h2 className="text-2xl font-semibold">À la carte</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
        {spaces.map((s, i) => (
          <div key={s.id} className={`relative rounded-3xl border-2 ${i === 1 ? "border-[#2B8B8E]" : "border-border"} p-7 bg-card`} data-testid={`tariff-${s.type}`}>
            {i === 1 && <span className="absolute -top-3 right-7 bg-[#2B8B8E] text-white text-xs font-semibold px-3 py-1 rounded-full">Populaire</span>}
            <p className="overline text-[#0D2C4D] mb-3">{spaceTypeLabel(s.type)}</p>
            <h3 className="text-2xl font-semibold mb-2">{s.name}</h3>
            <p className="text-sm text-muted-foreground mb-5">{s.description}</p>
            <div className="grid grid-cols-2 gap-3 mb-6">
              <div className="p-3 rounded-xl bg-muted/50 text-center">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Demi-journée</p>
                <p className="text-2xl font-bold text-[#2B8B8E] mt-1">{eur(s.price_half_day)}</p>
              </div>
              <div className="p-3 rounded-xl bg-muted/50 text-center">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Journée</p>
                <p className="text-2xl font-bold text-[#2B8B8E] mt-1">{eur(s.price_full_day)}</p>
              </div>
            </div>
            {s.type === "salle_reunion" && (
              <div className="mb-5 p-3 rounded-xl bg-[#0D2C4D] text-white text-center text-sm font-medium">
                Semaine (5 jours) : 450 €
              </div>
            )}
            <ul className="space-y-2 mb-6 text-sm">
              {s.equipments.slice(0, 5).map((e) => (
                <li key={e} className="flex items-center gap-2"><Check size={14} className="text-[#2B8B8E]" />{e}</li>
              ))}
            </ul>
            <Button asChild className="w-full bg-[#2B8B8E] hover:bg-[#1F6F73] text-white rounded-full" data-testid={`tariff-cta-${s.type}`}>
              <Link to={`/reserver?space=${s.id}`}>Réserver</Link>
            </Button>
          </div>
        ))}
      </div>

      {/* SECTION NOS PASS */}
      <div className="flex items-center gap-3 mb-6">
        <span className="w-9 h-9 rounded-full bg-[#2B8B8E] text-white text-sm font-bold flex items-center justify-center">2</span>
        <h2 className="text-2xl font-semibold">Nos Pass <span className="text-sm font-normal text-muted-foreground ml-2">Accès à l'espace coworking (Open Space)</span></h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-14">
        <div className="rounded-3xl border-2 border-border p-8 bg-card" data-testid="pass-nomade">
          <div className="flex items-center gap-4 mb-5">
            <div className="w-14 h-14 rounded-full bg-[#2B8B8E] flex items-center justify-center"><Backpack className="text-white" size={26} /></div>
            <div>
              <h3 className="text-2xl font-semibold">Pass Nomade</h3>
              <p className="text-sm text-muted-foreground">Accès Open Space</p>
            </div>
          </div>
          <p className="text-5xl font-light text-[#2B8B8E] mb-2">180 €</p>
          <p className="text-sm text-muted-foreground mb-1">10 journées d'accès</p>
          <p className="inline-block px-3 py-1 rounded-full bg-[#2B8B8E]/10 text-[#2B8B8E] text-xs font-semibold my-3">Valable 2 mois</p>
          <p className="text-sm text-muted-foreground mb-6">→ Soit 18 € la journée</p>
          <Button onClick={() => subscribePass("nomade")} className="w-full bg-[#2B8B8E] hover:bg-[#1F6F73] text-white rounded-full" data-testid="cta-pass-nomade">
            Payer 180 € par carte
          </Button>
        </div>

        <div className="rounded-3xl border-2 border-[#2B8B8E] p-8 bg-card relative" data-testid="pass-resident">
          <span className="absolute -top-3 right-7 bg-[#2B8B8E] text-white text-xs font-semibold px-3 py-1 rounded-full">Meilleur choix</span>
          <div className="flex items-center gap-4 mb-5">
            <div className="w-14 h-14 rounded-full bg-[#0D2C4D] flex items-center justify-center"><Armchair className="text-white" size={26} /></div>
            <div>
              <h3 className="text-2xl font-semibold">Pass Résident</h3>
              <p className="text-sm text-muted-foreground">Accès Open Space illimité</p>
            </div>
          </div>
          <p className="text-5xl font-light text-[#2B8B8E] mb-2">220 € <span className="text-lg text-muted-foreground">/ mois</span></p>
          <p className="text-sm font-semibold text-[#0D2C4D] mb-4">Accès illimité</p>
          <div className="p-4 rounded-xl bg-[#0D2C4D] text-white mb-4">
            <p className="font-semibold text-sm">1 MOIS OFFERT</p>
            <p className="text-xs opacity-90">Pour tout engagement annuel</p>
          </div>
          <p className="text-sm text-muted-foreground mb-1">2 420 € /an (12 mois dont 1 mois offert)</p>
          <p className="text-sm text-muted-foreground mb-6">→ Soit 202 € /mois</p>
          <Button onClick={() => subscribePass("resident")} className="w-full bg-[#2B8B8E] hover:bg-[#1F6F73] text-white rounded-full" data-testid="cta-pass-resident">
            Payer 220 € /mois par carte
          </Button>
        </div>
      </div>

      {/* SECTION INCLUS */}
      <div className="flex items-center gap-3 mb-6">
        <span className="w-9 h-9 rounded-full bg-[#2B8B8E] text-white text-sm font-bold flex items-center justify-center">3</span>
        <h2 className="text-2xl font-semibold">Inclus dans toutes nos offres</h2>
      </div>
      <div className="rounded-3xl border border-border p-8 bg-card grid grid-cols-2 md:grid-cols-5 gap-6 mb-10">
        {INCLUDED.map((it) => (
          <div key={it.label} className="text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-[#2B8B8E]/10 flex items-center justify-center mb-3">
              <it.icon className="text-[#2B8B8E]" size={22} />
            </div>
            <p className="text-xs font-medium uppercase tracking-wider">{it.label}</p>
          </div>
        ))}
      </div>

      <div className="text-center py-6">
        <p className="text-xl font-light italic text-[#0D2C4D]"><Sparkles className="inline mr-2 text-[#2B8B8E]" size={18} />Plus qu'un espace, une synergie !</p>
      </div>
    </div>
  );
}
